import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi, CreateCompanyInput, UpdateCompanyInput, ListQueryParams } from "../api/crmApi";
import { qk } from "../api/keys";

export function useCompaniesQuery(params: ListQueryParams = {}) {
    return useQuery({
        queryKey: [...qk.companies, params],
        queryFn: () => crmApi.listCompanies(params),
        placeholderData: keepPreviousData,
    });
}

export function useCompany(id: string) {
    return useQuery({
        queryKey: qk.company(id),
        queryFn: () => crmApi.getCompany(id),
        enabled: Boolean(id),
    });
}

export function useCreateCompany() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: CreateCompanyInput) => crmApi.createCompany(payload),
        meta: { suppressGlobalError: true },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: qk.companies });
        },
    });
}

export function useUpdateCompany() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: UpdateCompanyInput }) =>
            crmApi.updateCompany(id, payload),
        meta: { suppressGlobalError: true },
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: qk.companies });
            qc.invalidateQueries({ queryKey: qk.company(id) });
        },
    });
}

export function useDeleteCompany() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => crmApi.deleteCompany(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: qk.companies });
        },
    });
}

export function useBulkDeleteCompanies() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (ids: string[]) => crmApi.bulkDeleteCompanies(ids),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: qk.companies });
        },
    });
}

export function useRestoreCompany() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => crmApi.restoreCompany(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: qk.companies });
        },
    });
}

export function useCompanyContacts(companyId: string, params: ListQueryParams = {}) {
    return useQuery({
        queryKey: [...qk.company(companyId), "contacts", params],
        queryFn: () => crmApi.listCompanyContacts(companyId, params),
        enabled: Boolean(companyId),
    });
}

export function useCompanyDeals(companyId: string, params: ListQueryParams = {}) {
    return useQuery({
        queryKey: [...qk.company(companyId), "deals", params],
        queryFn: () => crmApi.listCompanyDeals(companyId, params),
        enabled: Boolean(companyId),
    });
}
